import { useMutation } from '@apollo/client';
import { useCallback } from 'react';

import { apolloClient } from '@/shared/api/apolloClient';
import { useAuthStore, type AuthUser } from '@/stores/authStore';
import { useBranchStore } from '@/stores/branchStore';

import { LOCK_SESSION_MUTATION, LOGIN_MUTATION, LOGOUT_MUTATION, UNLOCK_SESSION_MUTATION } from './auth.gql';

interface LoginVars {
  input: { email: string; password: string };
}
interface LoginResult {
  login: { access_token: string; expires_in: number; user: AuthUser };
}

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  const [mutate, state] = useMutation<LoginResult, LoginVars>(LOGIN_MUTATION);

  const submitLogin = useCallback(
    async (email: string, password: string) => {
      const { data, errors } = await mutate({ variables: { input: { email, password } } });
      if (errors && errors.length > 0) throw errors[0];
      if (!data?.login) throw new Error('login_failed');
      setSession(data.login.access_token, data.login.user);
      return data.login.user;
    },
    [mutate, setSession],
  );

  return { submitLogin, loading: state.loading, error: state.error };
}

export function useLogout() {
  const clearSession = useAuthStore((s) => s.clearSession);
  const [mutate] = useMutation(LOGOUT_MUTATION);

  return useCallback(async () => {
    try {
      await mutate();
    } finally {
      clearSession();
      await apolloClient.clearStore();
    }
  }, [mutate, clearSession]);
}

export function useLockSession() {
  const [mutate] = useMutation(LOCK_SESSION_MUTATION);
  const lock = useAuthStore((s) => s.lockSession);
  return useCallback(async () => {
    lock();
    await mutate().catch(() => undefined);
  }, [mutate, lock]);
}

export function useUnlockSession() {
  const [mutate] = useMutation(UNLOCK_SESSION_MUTATION);
  const unlock = useAuthStore((s) => s.unlockSession);
  return useCallback(async () => {
    await mutate();
    unlock();
  }, [mutate, unlock]);
}

export function hasPermission(permissions: ReadonlyArray<string>, key: string): boolean {
  return permissions.includes(key);
}

export function hasAnyRole(roles: ReadonlyArray<string>, allowed: ReadonlyArray<string>): boolean {
  return roles.some((r) => allowed.includes(r));
}

/**
 * The active multi-branch selection, but ONLY for administrators — non-admins always get null
 * so their pages send `branch_id: null` exactly as before (the branch_id list resolvers trust
 * the client arg, so a non-admin must never be able to emit a non-null one). Single safe read
 * path for every branch-aware page.
 */
export function useBranchScope(): string | null {
  const user = useAuthStore((s) => s.user);
  const selected_branch_id = useBranchStore((s) => s.selected_branch_id);
  if (!user || !hasAnyRole(user.roles, ['administrator'])) return null;
  return selected_branch_id;
}
